import React from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@/components/icons";
import { visibleTrail } from "@/core/navigation/trail";
import { usePublishedTrail } from "@/core/navigation/TrailDisplayContext";

/**
 * History trail under the header tabs: the drill-down chain the current
 * page was reached through. Hidden until a detail page publishes 2+
 * crumbs. Clicking a segment returns there; the stamped state carries only
 * the crumbs before it, so the trail truncates. Longer than 3 crumbs →
 * the head collapses into a non-interactive ellipsis.
 */
export const BreadcrumbTrail: React.FC = () => {
  const trail = usePublishedTrail();
  if (!trail || trail.length < 2) return null;

  const { ellipsed, visible } = visibleTrail(trail, 3);
  const offset = trail.length - visible.length;

  return (
    <nav aria-label="Breadcrumb" className="mt-3">
      <ol className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
        {ellipsed && (
          <li aria-hidden="true" className="flex items-center gap-2 shrink-0">
            <span>…</span>
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300" />
          </li>
        )}
        {visible.map((crumb, i) => {
          const index = offset + i;
          const isCurrent = index === trail.length - 1;
          return (
            <li key={index} className="flex items-center gap-2 min-w-0">
              {isCurrent ? (
                <span
                  aria-current="page"
                  className="bg-white rounded-full px-3 py-1 font-medium text-gray-900 truncate"
                >
                  {crumb.label}
                </span>
              ) : (
                <>
                  <Link
                    to={crumb.to}
                    state={{ trail: trail.slice(0, index) }}
                    className="bg-white rounded-full px-3 py-1 text-gray-500 hover:text-gray-900 transition-colors truncate"
                  >
                    {crumb.label}
                  </Link>
                  <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
